import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useCreateContentType } from '@wordpuppi/shared';
import type { FieldInput, FieldType } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/sites/$siteSlug/content-types/new')({
  component: NewContentTypePage,
});

const FIELD_TYPES: { value: FieldType; label: string; description: string }[] = [
  { value: 'text', label: 'Text', description: 'Single line text' },
  { value: 'long_text', label: 'Long Text', description: 'Multi-line plain text' },
  { value: 'rich_text', label: 'Rich Text', description: 'Formatted content with HTML' },
  { value: 'number', label: 'Number', description: 'Integer or decimal value' },
  { value: 'boolean', label: 'Boolean', description: 'True/false toggle' },
  { value: 'date', label: 'Date', description: 'Date without time' },
  { value: 'date_time', label: 'DateTime', description: 'Date with time' },
  { value: 'enum', label: 'Enum', description: 'Single choice from a list' },
  { value: 'json', label: 'JSON', description: 'Structured JSON data' },
  { value: 'image', label: 'Image', description: 'Image file reference' },
  { value: 'file', label: 'File', description: 'File attachment' },
  { value: 'relation', label: 'Relation', description: 'Link to another content type' },
  { value: 'url', label: 'URL', description: 'Web link' },
  { value: 'email', label: 'Email', description: 'Email address' },
  { value: 'slug', label: 'Slug', description: 'URL-safe identifier' },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function NewContentTypePage() {
  const { siteSlug } = Route.useParams();
  const navigate = useNavigate();
  const createContentType = useCreateContentType(siteSlug);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);
  const [icon, setIcon] = useState('');
  const [singleton, setSingleton] = useState(false);
  const [fields, setFields] = useState<FieldInput[]>([]);

  // New field form state
  const [showNewField, setShowNewField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<FieldType>('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    if (autoSlug) {
      setSlug(slugify(newName));
    }
  };

  const handleAddField = () => {
    if (!newFieldName.trim()) {
      toast.error('Field name is required');
      return;
    }

    const fieldSlug = slugify(newFieldName);
    if (fields.some((f) => f.slug === fieldSlug || slugify(f.name) === fieldSlug)) {
      toast.error('A field with this name already exists');
      return;
    }

    setFields([
      ...fields,
      {
        name: newFieldName,
        slug: fieldSlug,
        field_type: newFieldType,
        required: newFieldRequired,
      },
    ]);

    setNewFieldName('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setShowNewField(false);
  };

  const handleRemoveField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const result = await createContentType.mutateAsync({
        name: name.trim(),
        slug: slug || undefined,
        icon: icon || undefined,
        singleton,
        fields,
      });

      // Check if result contains an error (API returns 200 with error body)
      if (!result?.data?.data?.slug) {
        const errorMsg = (result as unknown as { error?: string })?.error || 'Failed to create content type';
        toast.error(errorMsg);
        return;
      }

      toast.success('Content type created');
      navigate({
        to: '/sites/$siteSlug/content-types/$typeSlug',
        params: { siteSlug, typeSlug: result.data.data.slug },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create content type');
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">New Content Type</h1>

      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            type="text"
            id="name"
            value={name}
            onChange={handleNameChange}
            placeholder="e.g., Projects"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <div className="flex gap-2 items-center">
            <Input
              type="text"
              id="slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setAutoSlug(false);
              }}
              placeholder="auto-generated from name"
              className="flex-1"
            />
            <label className="flex items-center gap-1 whitespace-nowrap text-sm">
              <input
                type="checkbox"
                checked={autoSlug}
                onChange={(e) => {
                  setAutoSlug(e.target.checked);
                  if (e.target.checked) {
                    setSlug(slugify(name));
                  }
                }}
                className="rounded border-input"
              />
              Auto
            </label>
          </div>
          <p className="text-sm text-muted-foreground">
            Table will be created as: wpp_{slug || 'slug'}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="icon">Icon</Label>
          <Input
            type="text"
            id="icon"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            placeholder="e.g. users"
            className="w-40"
          />
          <p className="text-sm text-muted-foreground">
            A lucide icon name (like <code className="text-xs">message-circle</code>) or an emoji. Optional.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="singleton"
            checked={singleton}
            onChange={(e) => setSingleton(e.target.checked)}
            className="rounded border-input"
          />
          <Label htmlFor="singleton" className="font-normal">
            Singleton (only one item can exist)
          </Label>
        </div>

        {/* Fields Section */}
        <div className="pt-6">
          <h3 className="text-lg font-medium mb-2">Fields</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Define the fields for this content type. You can add more fields later.
          </p>

          {fields.length > 0 && (
            <div className="rounded-lg border mb-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow key={index}>
                      <TableCell>{field.name}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {field.field_type}
                      </TableCell>
                      <TableCell>{field.required ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-destructive"
                          title="Remove field"
                          onClick={() => handleRemoveField(index)}
                        >
                          <Trash2 />
                          <span className="sr-only">Remove</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {showNewField ? (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Add Field</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Field Name</Label>
                  <Input
                    type="text"
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter commits the in-progress field instead of
                      // submitting the whole form and dropping it (AB#384)
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddField();
                      }
                    }}
                    placeholder="e.g., Title"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label>Field Type</Label>
                  <Select value={newFieldType} onValueChange={(v: string) => setNewFieldType(v as FieldType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((ft) => (
                        <SelectItem key={ft.value} value={ft.value}>
                          {ft.label} - {ft.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="newFieldRequired"
                    checked={newFieldRequired}
                    onChange={(e) => setNewFieldRequired(e.target.checked)}
                    className="rounded border-input"
                  />
                  <Label htmlFor="newFieldRequired" className="font-normal">
                    Required
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" onClick={handleAddField}>
                    Add Field
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowNewField(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowNewField(true)}
            >
              <Plus />
              Add Field
            </Button>
          )}
        </div>

        <div className="flex gap-2 pt-6">
          <Button type="submit" disabled={createContentType.isPending}>
            {createContentType.isPending ? 'Creating...' : 'Create Content Type'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate({ to: '/sites/$siteSlug/content-types', params: { siteSlug } })}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
